import { useMemo } from 'react';
import { message } from 'antd';
import { T, useTranslate } from '@tolgee/react';
import StatusButton from 'components/StatusButton';
import { getTalkInfo, updateConversationV2 } from 'services/api/conversation.convList';
import { useMakeConversationCache } from 'hooks/business/conversation';
import { searchOrderRef } from 'views/Conversation/ConvList/SearchPageItem';
import NonAssistLimitTip from '../NonAssistLimitTip';
import { useMemoizedFn } from 'ahooks';

export interface MessageEditorDisableProps {
  conversation: Conversation.Conversation;
  forceDisableText?: string;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
}

export interface WaConvStatusResult {
  text?: string;
  canSendTemplate: boolean;
}

export const useWaConvStatus = (conversation: Conversation.Conversation) => {
  const { t } = useTranslate();

  const waConvStatus = useMemo((): WaConvStatusResult => {
    const { channel, waConvInfo, waUserInfo } = conversation;
    let text: string | undefined;
    const canSendTemplate = false;
    const noLimit =
      waConvInfo?.existUserMsg && waConvInfo?.existConv && waUserInfo?.replyStatus === 'AGREE';
    if (channel !== 'WA_BIZ' || noLimit) {
      return {
        text,
        canSendTemplate: true,
      };
    }

    if (waUserInfo?.regStatus === 'UNREGISTERED') {
      return {
        text: t('WhatsApp.当前用户尚未注册Whatsapp'),
        canSendTemplate,
      };
    }
    if (waUserInfo?.replyStatus === 'DISAGREE') {
      return {
        text: t('WhatsApp.当前买家已拒绝了您的会话请求'),
        canSendTemplate,
      };
    }
    if (!waUserInfo?.optInStatus) {
      return {
        text: t('WhatsApp.新发起会话仅可发送一次模板话术，待用户响应后，才可自由发送内容'),
        canSendTemplate: true,
      };
    }
    if (waUserInfo?.replyStatus === 'NO_REPLY') {
      return {
        text: t('WhatsApp.当前买家尚未回应会话，暂不可发送消息'),
        canSendTemplate,
      };
    }
    if (!waConvInfo?.existUserMsg) {
      return {
        text: t('WhatsApp.用户24小时内未响应会话，当前仅可发送模板话术'),
        canSendTemplate: true,
      };
    }

    return {
      text,
      canSendTemplate,
    };
  }, [conversation, t]);

  return { waConvStatus };
};

/**
 * 消息编辑器禁用态与蒙层文案
 * 单一职责：根据渠道、接客权、拉黑、SIP、WhatsApp/美客多规则计算 disableText、waConvStatus、抢接客回调
 */
export function useMessageEditorDisable(props: MessageEditorDisableProps) {
  const { conversation: currentConversation, forceDisableText, forceAcquireControlReason } = props;
  const { conversationId, channel } = currentConversation;
  const { t } = useTranslate();
  const setConversation = useMakeConversationCache('set');
  const { waConvStatus } = useWaConvStatus(currentConversation);

  const handleAcquireClick = useMemoizedFn(() => {
    const _forceAcquireControlReason =
      forceAcquireControlReason || (searchOrderRef.current ? 'SEARCH_ORDER' : undefined);
    return updateConversationV2({
      conversationId,
      sourceAction: _forceAcquireControlReason
        ? (
            {
              PROCESS_TODO: 'TODO',
              PROCESS_REFUND_OR_RETURN: 'ORDER_MANAGEMENT',
              PROCESS_RATING: 'RATING',
              SEARCH_ORDER: 'SEARCH_ORDER',
            } as const
          )[_forceAcquireControlReason]
        : undefined,
      forceAcquireControl: true,
      forceAcquireControlReason: _forceAcquireControlReason,
    })
      .then(() => {
        return getTalkInfo({
          conversationId,
        }).then(setConversation);
      })
      .catch(() => {
        message.error(t('translation.操作失败，请稍后再试'));
      });
  });

  const disableText = useMemo(() => {
    if (forceDisableText) return forceDisableText;

    const {
      isAffiliateSip,
      blacklistFlag,
      canBeSent,
      controlStatus,
      nonAssistLimit = false,
      crossFulfillment,
    } = currentConversation;

    if (crossFulfillment) {
      return ' ';
    }

    if (nonAssistLimit) {
      return <NonAssistLimitTip conversation={currentConversation} />;
    }

    if (isAffiliateSip) {
      return `${t('ViewsChat.当前店铺为跨境SIP子店，无法进行回复。')}\n${t(
        '若您无需看到该店消息，可联系管理员将对应店铺移出客服组',
      )}`;
    }
    if (blacklistFlag) {
      return t('ViewsChat.已拉黑');
    }
    if (channel === 'LAZADA' && !canBeSent) {
      return t('ViewsChat.Lazada仅支持在买家下单7天内主动给未咨询过的买家发送消息');
    }
    if (channel === 'MERCADO' && !canBeSent) {
      return (
        <T
          keyName="美客多发送消息限制提示"
          params={{
            br: <br />,
          }}
        />
      );
    }

    if (!controlStatus) {
      const receptionName =
        currentConversation.receptionAssistantName || currentConversation.receptionName;

      if (receptionName) {
        return (
          <div>
            <T
              keyName="conversation.chatEditor.disabled.hint.occupied"
              params={{
                agentName: receptionName,
                btn: <StatusButton type="link" size="small" onClick={handleAcquireClick} />,
              }}
            />
          </div>
        );
      }

      return (
        <div>
          <T
            keyName="conversation.chatEditor.disabled.hint.unassigned"
            params={{
              btn: <StatusButton type="link" size="small" onClick={handleAcquireClick} />,
            }}
          />
        </div>
      );
    }
    if (waConvStatus.text) {
      return waConvStatus.text;
    }
    return '';
  }, [currentConversation, channel, t, waConvStatus, forceDisableText, handleAcquireClick]);

  return {
    disableText,
    waConvStatus,
    handleAcquireClick,
  };
}
